import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { toggleCustomSpinner } from 'apps/auth/actions/appAction';
import { addNextPayments, dispatchCheque } from 'apps/auth/actions/historyAction';
import { EmptyPage } from 'apps/auth/components/common/Layout/EmptyPage/EmptyPage';
import CloudFlareModal from 'apps/auth/components/common/Modal/CloudFlareModal';
import {
    ANTIFRAUD_ADDITIONAL_INFO_ID,
    ANTIFRAUD_CARD_VALIDATION_ID,
    ANTIFRAUD_INCORRECT_ADDRESS_ID,
    ANTIFRAUD_ONFIDO_ID,
    ANTIFRAUD_PURPOSE_TRANSFER_ID,
    ANTIFRAUD_RECIPIENT_DETAILS,
    ANTIFRAUD_RECIPIENT_ONFIDO_FLOW,
    ANTIFRAUD_RECIPIENT_RELATIONSHIP_ID,
    ANTIFRAUD_RELATIONSHIP_AND_SPLITTING_ID,
    ANTIFRAUD_SOURCE_OF_FUNDS_ID,
    ANTIFRAUD_INCREASE_LIMITS_ID,
    ANTIFRAUD_SOURCE_OF_INCOME_ID,
} from 'apps/auth/constants/AntifraudActions';
import { FILTER_FORM_NAME } from 'apps/auth/constants/formName';
import { KYC_COMPLETED, KYC_CONSIDER, KYC_DECLINED, KYC_NOT_STARTED, KYC_RETRY } from 'apps/auth/constants/KycStatuses';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logError } from 'apps/auth/helpers/clientLogger';
import { useApp, useExtra, useFields, useHistoryRecords, usePageMessages } from 'apps/auth/hooks/selectors';

import AwaitingPayments from './AwaitingPayments';
import HistoryRecord from './HistoryRecord';
import IncompletePaymentInfo from './IncompletePaymentInfo';
import { TransactionsHistoryList } from '../styles';

const GET_NEXT_PAYMENTS = '/auth/payments/history/next.page';

const SendHistory = (props) => {
    const { historyEmpty, labelVerifyIdentity, labelVerifyRecipient, labelVerifyCard, labelVerifyAddress, labelDetailsRequired, labelResumeTransfer } =
        usePageMessages();
    const records = useHistoryRecords();
    const { isShowCustomSpinner } = useApp();
    const fields = useFields(FILTER_FORM_NAME);

    const { incompletePayment, awaitingPayment, kycStatus, fraudKycStatus } = useExtra();

    const controllerRef = useRef(null);
    const landing = useRef(isShowCustomSpinner);
    const lastPaymentId = useRef(records?.lastPaymentId);

    useEffect(() => {
        document?.addEventListener('scroll', detectScrollToFooter);
        return () => {
            document?.removeEventListener('scroll', detectScrollToFooter);
        };
    }, []);

    useEffect(() => {
        lastPaymentId.current = records?.lastPaymentId;
    }, [records?.lastPaymentId]);

    useEffect(() => {
        landing.current = isShowCustomSpinner;
    }, [isShowCustomSpinner]);

    useEffect(() => {
        if (props.pageNumber) {
            addRecords();
        }
    }, [props.pageNumber]);

    const detectScrollToFooter = () => {
        const loadHeight = document.body.scrollHeight - window.innerHeight - 300;
        if (!landing.current && loadHeight < window.scrollY) {
            addRecords();
        }
    };

    const kycStatusMessages = {
        [KYC_NOT_STARTED]: labelVerifyIdentity,
        [KYC_COMPLETED]: labelResumeTransfer,
        [KYC_RETRY]: labelVerifyIdentity,
        [KYC_CONSIDER]: labelVerifyIdentity,
    };

    const fraudKycStatusMessages = {
        ...kycStatusMessages,
        [KYC_DECLINED]: labelVerifyIdentity,
    };

    const antifraudStatuses = {
        [ANTIFRAUD_PURPOSE_TRANSFER_ID]: labelDetailsRequired,
        [ANTIFRAUD_ADDITIONAL_INFO_ID]: labelDetailsRequired,
        [ANTIFRAUD_RECIPIENT_DETAILS]: labelVerifyRecipient,
        [ANTIFRAUD_CARD_VALIDATION_ID]: labelVerifyCard,
        [ANTIFRAUD_RELATIONSHIP_AND_SPLITTING_ID]: labelDetailsRequired,
        [ANTIFRAUD_SOURCE_OF_INCOME_ID]: labelDetailsRequired,
        [ANTIFRAUD_RECIPIENT_RELATIONSHIP_ID]: labelDetailsRequired,
        [ANTIFRAUD_SOURCE_OF_FUNDS_ID]: labelDetailsRequired,
        [ANTIFRAUD_INCREASE_LIMITS_ID]: labelDetailsRequired,
        [ANTIFRAUD_RECIPIENT_ONFIDO_FLOW]: labelVerifyRecipient,
        [ANTIFRAUD_INCORRECT_ADDRESS_ID]: labelVerifyAddress,
    };

    const addRecords = () => {
        if (lastPaymentId.current !== -1) {
            props.toggleCustomSpinner(true);
            apiFetchData(`${GET_NEXT_PAYMENTS}?lastId=${lastPaymentId.current}`, fields ? fields : {}, controllerRef)
                .then((json) => props.addNextPayments(json?.extra?.paymentHistory))
                .catch((e) => logError(e))
                .finally(() => props.toggleCustomSpinner(false));
        }
    };

    const getLabelText = (record) => {
        if (!record.draft) {
            return '';
        }

        const antifraudScenario = record.antifraudScenario;

        if (!antifraudScenario) {
            return kycStatus?.kyc_state ? kycStatusMessages[kycStatus.kyc_state] : '';
        }

        const actions = antifraudScenario?.actions;

        if (antifraudScenario.status === 'rejected') {
            return '';
        } else if (antifraudScenario.status === 'accepted') {
            return labelResumeTransfer;
        }

        const firstFraudAction = actions.reduce((min, action) => {
            return action.order < min.order ? action : min;
        }, actions[0]);

        if (firstFraudAction?.id === ANTIFRAUD_ONFIDO_ID) {
            return fraudKycStatus?.kyc_state ? fraudKycStatusMessages[fraudKycStatus.kyc_state] : '';
        }

        return antifraudStatuses[firstFraudAction?.id] || '';
    };

    const getMonthList = (records) => {
        let isFirst = true;
        return (
            <TransactionsHistoryList key={'list-' + records[0].paymentId} isNewMainScreen={props.isNewMainScreen}>
                {records.map((record) => {
                    const li = (
                        <HistoryRecord
                            record={record}
                            addMonth={isFirst}
                            key={record.paymentId}
                            label={getLabelText(record)}
                            isNewMainScreen={props.isNewMainScreen}
                        />
                    );
                    isFirst = false;
                    return li;
                })}
            </TransactionsHistoryList>
        );
    };

    const renderEmptyMessage = () => <EmptyPage iconName="ghost" emptyText={historyEmpty} />;
    const processJsonFunc = (dispatch, json) => props.dispatchCheque(dispatch, json);

    const showEmptyMessage = records && (!records?.payments || records?.payments?.length === 0) && historyEmpty;

    return (
        <>
            {incompletePayment && (
                <IncompletePaymentInfo popupRef={props.popupRef} modalRef={props.modalRef} record={incompletePayment} isNewMainScreen={props.isNewMainScreen} />
            )}
            {awaitingPayment?.length > 0 && (
                <AwaitingPayments
                    records={awaitingPayment}
                    alreadyHasAwaitingBlock={incompletePayment}
                    getDefaultLabelText={getLabelText}
                    isNewMainScreen={props.isNewMainScreen}
                />
            )}
            {records?.payments && records.payments.map((payment) => getMonthList(payment?.records, false))}
            {showEmptyMessage && renderEmptyMessage()}
            {props.isShowCloudFlareModal && <CloudFlareModal processJsonFunc={processJsonFunc} />}
        </>
    );
};

export default connect(null, { addNextPayments, toggleCustomSpinner, dispatchCheque })(SendHistory);
